import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Invitation, TenantDetail, TenantFeature, TranslationUsage } from '@up2d8/contracts';
import { LoadingState } from '@up2d8/ui';
import { useSession } from '../../shared/session';
import { useFileUrl, useUpload } from '../../shared/use-upload';
import { formatDate, formatDateTime, formatNumber } from '../../shared/format';
import {
  ActionsCell,
  AdminIcon,
  AdminPage,
  Badge,
  Btn,
  Card,
  CardHead,
  CardPad,
  DefinitionList,
  Empty,
  Field,
  Form,
  Input,
  Modal,
  Note,
  PageHead,
  Percent,
  SearchField,
  Select,
  Table,
  TabPanel,
  Tabs,
  Textarea,
  Toggle,
  Toolbar,
  useAdminToast,
  type TabItem,
} from '../admin-kit';
import { PlatformDemoReset } from './demo-tools';
import { FilterMenu } from './filter-menu';
import { DangerZoneTab, PeopleTab, SitesTab, TenantAuditTab } from './master-tabs';
import { OrgTypeCard } from './org-type-card';
import { SubscriptionTab } from './subscription-tab';
import { maandJaar, soortLabel, wanneer } from './platform-format';
import { UploadBox } from './upload-box';
import './huisstijl.css';

type TabKey =
  | 'details'
  | 'people'
  | 'subscription'
  | 'sites'
  | 'features'
  | 'invitations'
  | 'logo'
  | 'audit'
  | 'reset'
  | 'danger';

/**
 * Eén bedrijf (P3): gegevens, functies, uitnodigingen, logo en - bij een demobedrijf -
 * terugzetten, elk in een eigen tab.
 *
 * Elke handeling hier raakt een klant die er niet bij is. Daarom staat overal wat het
 * gevolg is — bij opschorten letterlijk: iedereen wordt afgemeld.
 */
export function TenantDetailPage() {
  const { t } = useTranslation();
  const { api } = useSession();
  const toast = useAdminToast();
  const queryClient = useQueryClient();
  const { tenantId } = useParams({ strict: false }) as { tenantId: string };
  const [tab, setTab] = useState<TabKey>('details');
  const [name, setName] = useState('');
  const [language, setLanguage] = useState<TenantDetail['defaultLanguage']>('nl');
  const [suspendOpen, setSuspendOpen] = useState(false);

  const tenant = useQuery({
    queryKey: ['platform', 'tenant', tenantId],
    queryFn: async () => {
      const { data, error } = await api.GET('/api/v1/platform/tenants/{tenantId}', {
        params: { path: { tenantId } },
      });
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return data as TenantDetail;
    },
  });

  // Hier en niet in de tab: het aantal staat in de tab "Functies".
  const features = useQuery({
    queryKey: ['platform', 'features', tenantId],
    queryFn: async () => {
      const { data, error } = await api.GET('/api/v1/platform/tenants/{tenantId}/features', {
        params: { path: { tenantId } },
      });
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return (data as { features: TenantFeature[] }).features;
    },
  });

  // De velden van "Gegevens" staan op de pagina: "Wijzigingen opslaan" staat in de paginakop.
  useEffect(() => {
    if (!tenant.data) return;
    setName(tenant.data.name);
    setLanguage(tenant.data.defaultLanguage);
  }, [tenant.data]);

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['platform'] });
  };

  const save = useMutation({
    mutationFn: async () => {
      const { error } = await api.PATCH('/api/v1/platform/tenants/{tenantId}', {
        params: { path: { tenantId } },
        body: { name: name.trim(), defaultLanguage: language } as never,
      });
      if (error) throw error;
    },
    onSuccess: async () => {
      toast.show(t('tenants.saved'));
      await refresh();
    },
  });

  const reactivate = useMutation({
    mutationFn: async () => {
      const { error } = await api.POST('/api/v1/platform/tenants/{tenantId}/reactivate', {
        params: { path: { tenantId } },
      });
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  if (!tenant.data) {
    return (
      <AdminPage>
        <PageHead
          title={t('nav.company')}
          crumb={{ label: t('tenants.title'), to: '/master/organisaties' }}
        />
        {tenant.isPending ? (
          <LoadingState rows={5} label={t('common.loading')} />
        ) : (
          <Note tone="danger" role="alert">
            {t('errors.generic')}
          </Note>
        )}
      </AdminPage>
    );
  }

  const detail = tenant.data;
  const demo = detail.kind === 'shared' || detail.kind === 'personal';
  const dirty =
    (name.trim() !== detail.name || language !== detail.defaultLanguage) && name.trim().length >= 2;

  const tabs: TabItem<TabKey>[] = [
    { key: 'details', label: t('beheer.platform.detail.tabDetails') },
    // MST-2: mensen, vestigingen, het spoor van de organisatie en de gevarenzone.
    { key: 'people', label: t('master.tabs.people') },
    // MST-6: pakket, status en de upsell-analyse van deze organisatie.
    { key: 'subscription', label: t('master.tabs.subscription') },
    { key: 'sites', label: t('master.tabs.sites') },
    { key: 'features', label: t('tenants.features'), count: features.data?.length },
    { key: 'invitations', label: t('tenants.invitations') },
    { key: 'logo', label: t('media.logo') },
    { key: 'audit', label: t('master.tabs.audit') },
    ...(demo ? [{ key: 'reset' as const, label: t('beheer.platform.detail.tabReset') }] : []),
    { key: 'danger', label: t('master.tabs.danger') },
  ];

  return (
    <AdminPage>
      <PageHead
        crumb={{ label: t('tenants.title'), to: '/master/organisaties' }}
        title={detail.name}
        sub={
          <>
            <span className="a-mono">{detail.code}</span>
            {' · '}
            {soortLabel(detail.kind, t).toLocaleLowerCase()}
            {' · '}
            {t('beheer.platform.detail.createdOn', { date: maandJaar(detail.createdAt) })}
          </>
        }
        actions={
          <>
            {detail.status !== 'active' ? (
              <Btn
                variant="sec"
                disabled={reactivate.isPending}
                onClick={() => reactivate.mutate()}
              >
                {t('tenants.reactivate')}
              </Btn>
            ) : (
              <Btn variant="danger-sec" onClick={() => setSuspendOpen(true)}>
                {t('tenants.suspendConfirm')}
              </Btn>
            )}
            <Btn
              icon={<AdminIcon name="check" size={16} />}
              disabled={!dirty || save.isPending}
              onClick={() => save.mutate()}
            >
              {t('tenants.save')}
            </Btn>
          </>
        }
      />

      {detail.status !== 'active' ? (
        <Note
          tone="attention"
          lead={`${detail.status === 'archived' ? t('master.archivedSince') : t('tenants.suspendedSince')} ${detail.suspendedAt ? formatDateTime(detail.suspendedAt) : '—'}`}
        >
          {detail.suspendedReason ? `— ${detail.suspendedReason}` : null}
        </Note>
      ) : null}
      {save.isError || reactivate.isError ? (
        <Note tone="danger" role="alert">
          {t('errors.generic')}
        </Note>
      ) : null}

      <Tabs label={detail.name} idPrefix="tenant" tabs={tabs} value={tab} onChange={setTab} />

      <TabPanel idPrefix="tenant" tabKey={tab} className="a-stack">
        {tab === 'details' ? (
          <>
            <DetailsCard
              tenant={detail}
              name={name}
              language={language}
              onName={setName}
              onLanguage={setLanguage}
              onSubmit={() => dirty && save.mutate()}
            />
            <OrgTypeCard tenant={detail} onChanged={refresh} />
            <TranslationUsageCard tenantId={tenantId} />
          </>
        ) : tab === 'features' ? (
          <FeaturesTab tenantId={tenantId} features={features.data} pending={features.isPending} />
        ) : tab === 'invitations' ? (
          <InvitationsTab tenantId={tenantId} onToast={toast.show} />
        ) : tab === 'logo' ? (
          <LogoTab tenant={detail} onChanged={refresh} onToast={toast.show} />
        ) : tab === 'people' ? (
          <PeopleTab tenantId={tenantId} onToast={toast.show} />
        ) : tab === 'subscription' ? (
          <SubscriptionTab tenantId={tenantId} onToast={toast.show} />
        ) : tab === 'sites' ? (
          <SitesTab tenantId={tenantId} />
        ) : tab === 'audit' ? (
          <TenantAuditTab tenantId={tenantId} />
        ) : tab === 'danger' ? (
          <DangerZoneTab tenant={detail} onChanged={refresh} onToast={toast.show} />
        ) : (
          <PlatformDemoReset tenant={detail} />
        )}
      </TabPanel>

      <SuspendModal
        open={suspendOpen}
        tenantId={tenantId}
        onClose={() => setSuspendOpen(false)}
        onDone={refresh}
      />
      {toast.element}
    </AdminPage>
  );
}

/** Tab Gegevens: naam, code (vast) en standaardtaal, met wat er verder over het bedrijf bekend is. */
function DetailsCard({
  tenant,
  name,
  language,
  onName,
  onLanguage,
  onSubmit,
}: {
  tenant: TenantDetail;
  name: string;
  language: TenantDetail['defaultLanguage'];
  onName: (value: string) => void;
  onLanguage: (value: TenantDetail['defaultLanguage']) => void;
  onSubmit: () => void;
}) {
  const { t } = useTranslation();

  return (
    <Card>
      <CardHead
        title={t('tenants.detail')}
        actions={
          <Badge tone={tenant.status === 'active' ? 'ok' : 'attention'}>
            {tenant.status === 'active'
              ? t('tenants.active')
              : tenant.status === 'archived'
                ? t('master.archived')
                : t('tenants.suspended')}
          </Badge>
        }
      />
      <CardPad>
        <Form onSubmit={onSubmit}>
          <Field label={t('tenants.name')}>
            {(props) => (
              <Input {...props} value={name} onChange={(event) => onName(event.target.value)} />
            )}
          </Field>
          {/* De code ligt vast: medewerkers typen hem over om aan te melden. */}
          <Field label={t('tenants.code')} hint={t('tenants.codeHint')}>
            {(props) => <Input {...props} className="a-mono" value={tenant.code} readOnly />}
          </Field>
          <Field label={t('tenants.language')}>
            {(props) => (
              <Select
                {...props}
                value={language}
                onChange={(event) => onLanguage(event.target.value as typeof language)}
              >
                <option value="nl">Nederlands</option>
                <option value="fr">Français</option>
                <option value="en">English</option>
              </Select>
            )}
          </Field>
        </Form>
        <DefinitionList
          items={[
            [t('tenants.kind'), soortLabel(tenant.kind, t)],
            [t('tenants.owner'), tenant.ownerEmail ?? '—'],
            [t('tenants.lastActivity'), wanneer(tenant.lastActivityAt, t)],
            [t('tenants.created'), formatDate(tenant.createdAt)],
          ]}
        />
      </CardPad>
    </Card>
  );
}

/**
 * Wat dit bedrijf aan vertalen verbruikt.
 *
 * Het staat hier omdat het geld kost en omdat het één van de weinige getallen is die iets zeggen
 * zonder dat je de klant kent: een bedrijf dat plots tien keer zo veel vertaalt, heeft meestal iets
 * anders aan de hand — een import die berichten publiceert, of een job die blijft herhalen.
 *
 * Boven de grens krijgt de lezer het origineel, en dat staat er ook: de balk is er om te zien
 * hoeveel ruimte er nog is, niet om iemand te betrappen.
 */
function TranslationUsageCard({ tenantId }: { tenantId: string }) {
  const { t } = useTranslation();
  const { api, has } = useSession();

  const usage = useQuery({
    queryKey: ['platform', 'translation-usage', tenantId],
    enabled: has('translation.usage.view'),
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/platform/tenants/{tenantId}/translation-usage', {
        params: { path: { tenantId } },
      });
      return (data as TranslationUsage | undefined) ?? null;
    },
  });

  if (!has('translation.usage.view')) return null;

  const limit = usage.data?.limitCharacters ?? 0;
  const used = usage.data?.currentMonthCharacters ?? 0;
  const share = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;

  return (
    <Card>
      <CardHead title={t('translationUsage.title')} />
      <CardPad>
        <p className="a-muted">{t('translationUsage.intro')}</p>
        {usage.isPending ? (
          <LoadingState rows={3} label={t('common.loading')} />
        ) : (usage.data?.months.length ?? 0) === 0 ? (
          <p className="a-muted">{t('translationUsage.none')}</p>
        ) : (
          <>
            <p>
              {limit > 0
                ? t('translationUsage.thisMonth', {
                    used: formatNumber(used),
                    limit: formatNumber(limit),
                  })
                : t('translationUsage.thisMonthNoLimit', { used: formatNumber(used) })}
            </p>
            {limit > 0 ? <Percent value={share} /> : null}
          </>
        )}
      </CardPad>
      {(usage.data?.months.length ?? 0) > 0 ? (
        <Table label={t('translationUsage.title')}>
          <thead>
            <tr>
              <th>{t('translationUsage.month')}</th>
              <th className="num">{t('translationUsage.characters')}</th>
              <th className="num">{t('translationUsage.requests')}</th>
            </tr>
          </thead>
          <tbody>
            {usage.data?.months.map((month) => (
              <tr key={month.month}>
                <td>{month.month}</td>
                <td className="num">{formatNumber(month.characters)}</td>
                <td className="num">{formatNumber(month.requests)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : null}
    </Card>
  );
}

type ToonFilter = 'all' | 'changed';

/**
 * Tab Functies: per functie de uitleg, de module, de technische sleutel, Default of Set, en de
 * schakelaar. Een schakelaar zet de functie meteen, zoals voorheen; "Terug naar standaard" haalt
 * de instelling voor dit bedrijf weg.
 */
function FeaturesTab({
  tenantId,
  features,
  pending,
}: {
  tenantId: string;
  features: TenantFeature[] | undefined;
  pending: boolean;
}) {
  const { t } = useTranslation();
  const { api } = useSession();
  const queryClient = useQueryClient();
  const [zoek, setZoek] = useState('');
  const [module, setModule] = useState('all');
  const [toon, setToon] = useState<ToonFilter>('all');

  const update = useMutation({
    mutationFn: async (change: Record<string, boolean | null>) => {
      const { error } = await api.PUT('/api/v1/platform/tenants/{tenantId}/features', {
        params: { path: { tenantId } },
        body: { features: change } as never,
      });
      if (error) throw error;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['platform', 'features', tenantId] });
    },
  });

  const modules = [...new Set((features ?? []).map((feature) => feature.module))].sort();
  const term = zoek.trim().toLowerCase();
  const zichtbaar = (features ?? []).filter(
    (feature) =>
      (module === 'all' || feature.module === module) &&
      (toon === 'all' || feature.source === 'tenant') &&
      (!term ||
        feature.key.toLowerCase().includes(term) ||
        feature.description.toLowerCase().includes(term)),
  );

  return (
    <Card>
      <Toolbar>
        <SearchField label={t('beheer.platform.features.search')} value={zoek} onChange={setZoek} />
        <FilterMenu
          label={t('beheer.platform.features.module')}
          value={module}
          onChange={setModule}
          options={[
            { value: 'all', label: t('beheer.platform.filters.all') },
            ...modules.map((value) => ({ value, label: value })),
          ]}
        />
        <FilterMenu<ToonFilter>
          label={t('beheer.platform.features.show')}
          value={toon}
          onChange={setToon}
          showValue
          options={[
            { value: 'all', label: t('beheer.platform.filters.all') },
            { value: 'changed', label: t('beheer.platform.features.onlyChanged') },
          ]}
        />
      </Toolbar>
      <CardPad>
        <p className="a-muted">{t('tenants.featuresIntro')}</p>
        {update.isError ? (
          <Note tone="danger" role="alert">
            {t('errors.generic')}
          </Note>
        ) : null}
        {pending ? (
          <LoadingState rows={3} label={t('common.loading')} />
        ) : zichtbaar.length === 0 ? (
          <Empty
            icon={<AdminIcon name="search" size={24} />}
            title={t('beheer.platform.features.none')}
          />
        ) : (
          // MST-4: per module gegroepeerd, met een hoofdschakelaar die de hele module in één keer zet.
          <div className="a-stack">
            {[...new Set(zichtbaar.map((feature) => feature.module))].sort().map((naam) => {
              const groep = zichtbaar.filter((feature) => feature.module === naam);
              const allesAan = groep.every((feature) => feature.enabled);
              const allesUit = groep.every((feature) => !feature.enabled);
              const zet = (enabled: boolean) =>
                update.mutate(Object.fromEntries(groep.map((feature) => [feature.key, enabled])));
              return (
                <section key={naam} data-testid={`feature-module-${naam}`}>
                  <div className="a-item-top">
                    <h3 className="a-h3">{t('master.features.module', { module: naam })}</h3>
                    <Badge>
                      {t('master.features.onCount', {
                        on: groep.filter((feature) => feature.enabled).length,
                        total: groep.length,
                      })}
                    </Badge>
                    <span style={{ flex: 1 }} />
                    <Btn
                      variant="ghost"
                      size="sm"
                      disabled={update.isPending || allesAan}
                      onClick={() => zet(true)}
                    >
                      {t('master.features.allOn')}
                    </Btn>
                    <Btn
                      variant="ghost"
                      size="sm"
                      disabled={update.isPending || allesUit}
                      onClick={() => zet(false)}
                    >
                      {t('master.features.allOff')}
                    </Btn>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    {groep.map((feature) => (
                      <FeatureRow
                        key={feature.key}
                        feature={feature}
                        busy={update.isPending}
                        onToggle={(enabled) => update.mutate({ [feature.key]: enabled })}
                        onReset={() => update.mutate({ [feature.key]: null })}
                      />
                    ))}
                  </div>
                </section>
              );
            })}
          </div>
        )}
      </CardPad>
    </Card>
  );
}

/** Eén functie als `.a-switch`: titel met badge en module, uitleg, sleutel; rechts de schakelaar. */
function FeatureRow({
  feature,
  busy,
  onToggle,
  onReset,
}: {
  feature: TenantFeature;
  busy: boolean;
  onToggle: (enabled: boolean) => void;
  onReset: () => void;
}) {
  const { t } = useTranslation();
  const id = useId();
  const set = feature.source === 'tenant';

  return (
    <div className="a-switch" data-testid={`feature-${feature.key}`}>
      <div className="a-switch-text">
        <div className="a-item-top">
          <span className="a-switch-title" id={`${id}-title`}>
            {feature.description}
          </span>
          <Badge tone={set ? 'info' : 'neutral'}>
            {set
              ? t('beheer.platform.features.set')
              : feature.source === 'platform'
                ? t('master.featuresPage.byMaster')
                : t('beheer.platform.features.default')}
          </Badge>
          <span className="a-meta">{feature.module}</span>
        </div>
        <div className="a-mono a-meta" id={`${id}-key`}>
          {feature.key}
          {feature.owner ? ` · ${feature.owner}` : ''}
        </div>
      </div>
      {set ? (
        <Btn variant="ghost" size="sm" disabled={busy} onClick={onReset}>
          {t('tenants.featureReset')}
        </Btn>
      ) : null}
      <Toggle
        checked={feature.enabled}
        onChange={onToggle}
        labelledBy={`${id}-title`}
        describedBy={`${id}-key`}
        disabled={busy}
      />
    </div>
  );
}

/**
 * Tab Uitnodigingen: wie als beheerder uitgenodigd is, en opnieuw versturen voor wie nog niet
 * aanvaardde of wiens mail niet vertrok.
 */
function InvitationsTab({
  tenantId,
  onToast,
}: {
  tenantId: string;
  onToast: (message: string) => void;
}) {
  const { t } = useTranslation();
  const { api } = useSession();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');

  const invitations = useQuery({
    queryKey: ['platform', 'invitations', tenantId],
    queryFn: async () => {
      const { data, error } = await api.GET('/api/v1/platform/tenants/{tenantId}/invitations', {
        params: { path: { tenantId } },
      });
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return (data as { items: Invitation[] }).items;
    },
  });

  const invite = useMutation({
    mutationFn: async () => {
      const { error } = await api.POST('/api/v1/platform/tenants/{tenantId}/invitations', {
        params: { path: { tenantId } },
        body: { email: email.trim(), displayName: name.trim(), roleHint: 'admin' } as never,
      });
      if (error) throw error;
    },
    onSuccess: async () => {
      onToast(t('tenants.inviteSent', { email: email.trim() }));
      setOpen(false);
      setEmail('');
      setName('');
      await queryClient.invalidateQueries({ queryKey: ['platform', 'invitations', tenantId] });
    },
  });

  const resend = useMutation({
    mutationFn: async (invitationId: string) => {
      const { error } = await api.POST(
        '/api/v1/platform/tenants/{tenantId}/invitations/{invitationId}/resend',
        { params: { path: { tenantId, invitationId } } },
      );
      if (error) throw error;
    },
    onSuccess: async () => {
      onToast(t('beheer.platform.invitations.resent'));
      await queryClient.invalidateQueries({ queryKey: ['platform', 'invitations', tenantId] });
    },
  });

  const klaar = email.includes('@') && name.trim().length >= 2;

  return (
    <Card>
      <CardHead
        title={t('tenants.invitations')}
        count={invitations.data?.length}
        actions={
          <Btn
            variant="sec"
            size="sm"
            icon={<AdminIcon name="plus" size={16} />}
            onClick={() => {
              invite.reset();
              setOpen(true);
            }}
          >
            {t('tenants.invite')}
          </Btn>
        }
      />

      {resend.isError ? (
        <CardPad>
          <Note tone="danger" role="alert">
            {t('errors.generic')}
          </Note>
        </CardPad>
      ) : null}

      {invitations.isPending ? (
        <LoadingState rows={2} label={t('common.loading')} />
      ) : invitations.isError ? (
        <CardPad>
          <Note tone="danger" role="alert">
            {t('errors.generic')}
          </Note>
        </CardPad>
      ) : (invitations.data?.length ?? 0) === 0 ? (
        <Empty icon={<AdminIcon name="mail" size={24} />} title={t('tenants.noInvitations')} />
      ) : (
        <Table label={t('tenants.invitations')}>
          <thead>
            <tr>
              <th>{t('tenants.inviteName')}</th>
              <th>{t('tenants.inviteEmail')}</th>
              <th>{t('tenants.status')}</th>
              <th>{t('tenants.inviteExpires')}</th>
              <th className="act">
                <span className="a-sr">{t('beheer.platform.invitations.actions')}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {invitations.data?.map((invitation) => (
              <tr key={invitation.id}>
                <td>
                  <span className="a-name">{invitation.displayName}</span>
                </td>
                <td>{invitation.email}</td>
                <td>
                  <InvitationBadge invitation={invitation} />
                </td>
                <td>{formatDateTime(invitation.expiresAt)}</td>
                {invitation.acceptedAt || invitation.status === 'revoked' ? (
                  <td />
                ) : (
                  <ActionsCell>
                    <Btn
                      variant="ghost"
                      size="sm"
                      disabled={resend.isPending}
                      testId="platform-invitation-resend"
                      onClick={() => resend.mutate(invitation.id)}
                    >
                      {invitation.mailStatus === 'failed'
                        ? t('invitations.retry')
                        : t('invitations.resend')}
                    </Btn>
                  </ActionsCell>
                )}
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={t('tenants.invite')}
        footer={
          <>
            <Btn variant="sec" onClick={() => setOpen(false)}>
              {t('common.cancel')}
            </Btn>
            <Btn disabled={!klaar || invite.isPending} onClick={() => invite.mutate()}>
              {t('tenants.invite')}
            </Btn>
          </>
        }
      >
        <Form onSubmit={() => klaar && invite.mutate()}>
          <Field label={t('tenants.inviteName')}>
            {(props) => (
              <Input {...props} value={name} onChange={(event) => setName(event.target.value)} />
            )}
          </Field>
          <Field
            label={t('tenants.inviteEmail')}
            error={invite.isError ? t('errors.generic') : undefined}
          >
            {(props) => (
              <Input
                {...props}
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            )}
          </Field>
        </Form>
      </Modal>
    </Card>
  );
}

/** Aanvaard, wacht op antwoord, verlopen, ingetrokken - of de mail vertrok niet. */
function InvitationBadge({ invitation }: { invitation: Invitation }) {
  const { t } = useTranslation();
  if (invitation.acceptedAt) return <Badge tone="ok">{t('tenants.inviteAccepted')}</Badge>;
  if (invitation.status === 'revoked') {
    return <Badge>{t('beheer.platform.invitations.revoked')}</Badge>;
  }
  if (invitation.status === 'expired') {
    return <Badge>{t('beheer.platform.invitations.expired')}</Badge>;
  }
  if (invitation.mailStatus === 'failed') {
    return <Badge tone="danger">{t('beheer.platform.invitations.mailFailed')}</Badge>;
  }
  return <Badge tone="attention">{t('tenants.invitePending')}</Badge>;
}

/**
 * Tab Logo: het logo van het bedrijf.
 *
 * Dit is het enige bestand dat een platformbeheerder van UP2D8 beheert, en het enige dat
 * zichtbaar is vóórdat iemand is aangemeld: het staat op het aanmeldscherm van de app, zodra
 * een medewerker de bedrijfscode heeft ingevuld. Dat maakt het meteen duidelijk dat hij bij
 * het juiste bedrijf is — en dat is precies waarom het hier staat en niet bij de klant zelf.
 */
function LogoTab({
  tenant,
  onChanged,
  onToast,
}: {
  tenant: TenantDetail;
  onChanged: () => Promise<void>;
  onToast: (message: string) => void;
}) {
  const { t } = useTranslation();
  const { api } = useSession();
  const upload = useUpload();
  const fileUrl = useFileUrl();
  const [logoUrl, setLogoUrl] = useState<string | null>(null);

  // De URL is een minuut geldig; hem bij het bedrijf bewaren zou een dode link opleveren.
  useEffect(() => {
    if (!tenant.logoFileId) {
      setLogoUrl(null);
      return;
    }
    let current = true;
    void fileUrl(tenant.logoFileId).then((url) => {
      if (current) setLogoUrl(url);
    });
    return () => {
      current = false;
    };
  }, [tenant.logoFileId, fileUrl]);

  const setLogo = useMutation({
    mutationFn: async (fileId: string | null) => {
      const { error } = await api.PUT('/api/v1/platform/tenants/{tenantId}/logo', {
        params: { path: { tenantId: tenant.id } },
        body: { fileId } as never,
      });
      if (error) throw error;
    },
    onSuccess: async (_result, fileId) => {
      onToast(fileId ? t('media.logoSaved') : t('media.logoRemoved'));
      await onChanged();
    },
  });

  const onSelect = async (file: File): Promise<void> => {
    // De tenant staat er expliciet bij: een platformbeheerder heeft er zelf geen in zijn
    // sessie, en een logo hoort bij één bedrijf.
    const fileId = await upload.upload(file, {
      ownerModule: 'tenants',
      purpose: 'tenant_logo',
      tenantId: tenant.id,
    });
    if (fileId) setLogo.mutate(fileId);
  };

  return (
    <Card>
      <CardHead title={t('media.logo')} />
      <CardPad>
        <div style={{ maxWidth: 360 }}>
          <UploadBox
            label={t('media.logo')}
            hint={t('media.logoHint')}
            accept="image/*"
            hasImage={Boolean(tenant.logoFileId)}
            preview={
              logoUrl ? (
                <img src={logoUrl} alt={t('media.logoAlt', { name: tenant.name })} />
              ) : (
                <span className="a-muted">{t('media.noLogo')}</span>
              )
            }
            progress={upload.progress}
            error={upload.error ?? (setLogo.isError ? t('errors.generic') : null)}
            busy={setLogo.isPending}
            onSelect={(file) => void onSelect(file)}
            onCancel={upload.cancel}
            onRemove={() => setLogo.mutate(null)}
            removeLabel={t('media.logoRemove')}
          />
        </div>
      </CardPad>
    </Card>
  );
}

/** Opschorten, met een reden: iedereen van het bedrijf wordt afgemeld. */
function SuspendModal({
  open,
  tenantId,
  onClose,
  onDone,
}: {
  open: boolean;
  tenantId: string;
  onClose: () => void;
  onDone: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const { api } = useSession();
  const [reason, setReason] = useState('');

  const suspend = useMutation({
    mutationFn: async () => {
      const { error } = await api.POST('/api/v1/platform/tenants/{tenantId}/suspend', {
        params: { path: { tenantId } },
        body: { reason: reason.trim() } as never,
      });
      if (error) throw error;
    },
    onSuccess: async () => {
      onClose();
      setReason('');
      await onDone();
    },
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('tenants.suspendTitle')}
      sub={t('tenants.suspendExplain')}
      footer={
        <>
          <Btn variant="sec" onClick={onClose}>
            {t('common.cancel')}
          </Btn>
          <Btn
            variant="danger"
            disabled={reason.trim().length < 3 || suspend.isPending}
            onClick={() => suspend.mutate()}
          >
            {t('tenants.suspendConfirm')}
          </Btn>
        </>
      }
    >
      <Field
        label={t('tenants.suspendReason')}
        error={suspend.isError ? t('errors.generic') : undefined}
      >
        {(props) => (
          <Textarea {...props} value={reason} onChange={(event) => setReason(event.target.value)} />
        )}
      </Field>
    </Modal>
  );
}
